import React, { useEffect, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import { Icon } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { TrackedObject } from '../types';
import { formatDistance } from 'date-fns';
import { eventEmitter, EVENTS } from '../events/EventEmitter';

// Fix for default marker icons in Leaflet with webpack
// Need to import marker icon images
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

// Create custom icons for Leaflet
const defaultIcon = new Icon({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  tooltipAnchor: [16, -28],
  shadowSize: [41, 41]
});

// Green marker for the latest location
const greenIcon = new Icon({
  iconUrl: 'https://static.vecteezy.com/system/resources/previews/052/259/574/non_2x/3d-bus-icon-with-a-realistic-metallic-finish-isolated-on-a-transparent-background-free-png.png',
  shadowUrl: markerShadow,
  iconSize: [64, 64],
  iconAnchor: [32, 32],
  popupAnchor: [1, -34],
  tooltipAnchor: [16, -28],
  shadowSize: [41, 41]
});

// Gray marker for historical locations
const grayIcon = new Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-grey.png',
  shadowUrl: markerShadow,
  iconSize: [20, 33], // Slightly smaller for history points
  iconAnchor: [10, 33],
  popupAnchor: [1, -34],
  tooltipAnchor: [16, -28],
  shadowSize: [41, 41]
});

// Function to create a custom icon from a URL
const createCustomIcon = (url: string): Icon => {
  return new Icon({
    iconUrl: url,
    shadowUrl: markerShadow,
    iconSize: [40, 40],  // Good size for most custom icons
    iconAnchor: [20, 40], // Bottom center of the icon
    popupAnchor: [0, -40],
    shadowSize: [41, 41]
  });
};

// Component to recenter map when selectedObject changes
const RecenterMap: React.FC<{ 
  object?: TrackedObject, 
  animate?: boolean 
}> = ({ object, animate = true }) => {
  const map = useMap();
  const positionRef = useRef<[number, number] | null>(null);
  
  // OpenStreetMap typically supports zoom levels from 0-19
  // 0: World view
  // 19: Closest street level view
  const MAX_ZOOM_LEVEL = 18; // Using 18 as it's well-supported across most areas
  
  useEffect(() => {
    if (object) {
      const newPosition: [number, number] = [object.latitude, object.longitude];
      const positionChanged = !positionRef.current || 
        positionRef.current[0] !== newPosition[0] || 
        positionRef.current[1] !== newPosition[1];
      
      // Update the map view if position changed
      if (positionChanged) {
        // Use the maximum supported zoom level when focusing on an object
        // This provides the closest view supported by OpenStreetMap
        map.setView(newPosition, animate ? MAX_ZOOM_LEVEL : 13, {
          animate
        });
        positionRef.current = newPosition;
      }
    }
  }, [map, object, animate]);
  
  return null;
};

interface MapProps {
  objects: TrackedObject[];
  selectedObjectId?: string;
  onSelectObject?: (id: string) => void;
}

const MapComponent: React.FC<MapProps> = ({ 
  objects: initialObjects, 
  selectedObjectId,
  onSelectObject
}) => {
  const mapRef = useRef(null);
  // Use local state to track objects so we can update them from WebSocket events
  const [objects, setObjects] = useState<TrackedObject[]>(initialObjects);
  const selectedObject = objects.find(obj => obj.id === selectedObjectId);
  
  // Update local objects when props change
  useEffect(() => {
    setObjects(initialObjects);
  }, [initialObjects]);
  
  // Listen for real-time location updates
  useEffect(() => {
    // Subscribe to location update events
    const unsubscribe = eventEmitter.subscribe(EVENTS.LOCATION_UPDATED, (updatedObject: TrackedObject) => {
      setObjects(currentObjects => {
        // Find and replace the updated object
        const index = currentObjects.findIndex(obj => obj.id === updatedObject.id);
        if (index !== -1) {
          const newObjects = [...currentObjects];
          newObjects[index] = updatedObject;
          return newObjects;
        }
        return currentObjects;
      });
    });
    
    // Clean up subscription when component unmounts
    return () => unsubscribe();
  }, []);
  
  return (
    <div className="map-container">
      <MapContainer 
        center={selectedObject ? [selectedObject.latitude, selectedObject.longitude] : [37.7749, -122.4194]}
        zoom={13} 
        style={{ height: '100%', width: '100%' }}
        ref={mapRef}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />
        
        {/* First render all historical markers (gray) so they'll be underneath */}
        {objects.map(object => (
          selectedObjectId === object.id && object.locationHistory && object.locationHistory.length > 0 && (
            <React.Fragment key={`${object.id}-history`}>
              {object.locationHistory.slice(0, -1).map((histPoint, index) => (
                <Marker 
                  key={`${object.id}-history-${index}`}
                  position={[histPoint.latitude, histPoint.longitude]}
                  icon={grayIcon}
                  zIndexOffset={-1000} // Ensure historical points are below
                >
                  <Popup>
                    <div>
                      <h4>Historical Point</h4>
                      <p>Object: {object.name}</p>
                      <p>
                        Time: {formatDistance(histPoint.timestamp, new Date(), { addSuffix: true })}
                      </p>
                      <p>
                        Coordinates: {histPoint.latitude.toFixed(6)}, {histPoint.longitude.toFixed(6)}
                      </p>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </React.Fragment>
          )
        ))}
        
        {/* Then render all current location markers (custom/green/blue) so they'll be on top */}
        {objects.map(object => {
          // Determine which icon to use
          let iconToUse;
          
          if (selectedObjectId === object.id) {
            // For selected object, use custom icon if available, otherwise use green icon
            iconToUse = object.iconUrl ? createCustomIcon(object.iconUrl) : greenIcon;
          } else {
            // For other objects, use default icon
            iconToUse = defaultIcon;
          }
          
          return (
            <Marker 
              key={object.id}
              position={[object.latitude, object.longitude]}
              icon={iconToUse}
              zIndexOffset={selectedObjectId === object.id ? 1000 : 500} // Selected object gets highest z-index
              eventHandlers={{
                click: () => {
                  if (onSelectObject) {
                    onSelectObject(object.id);
                  }
                }
              }}
            >
            <Popup>
              <div>
                <h3>{object.name}</h3>
                {object.description && <p>{object.description}</p>}
                <p>
                  Last updated: {formatDistance(object.lastUpdate, new Date(), { addSuffix: true })}
                </p>
                <p>
                  Coordinates: {object.latitude.toFixed(6)}, {object.longitude.toFixed(6)}
                </p>
              </div>
            </Popup>
            </Marker>
          );
        })}
        
        {selectedObject && (
          <RecenterMap 
            key={`${selectedObject.id}-${selectedObject.latitude}-${selectedObject.longitude}`}
            object={selectedObject} 
          />
        )}
      </MapContainer>
    </div>
  );
};

export default MapComponent;
